import { NavLink, useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import {
  LayoutDashboard, Users, Package, ShoppingCart, FileText, Receipt,
  Building2, Truck, ChefHat, Factory, BarChart3, DollarSign, CreditCard,
  Briefcase, UserCog, Settings, Shield, Database, Bell, Heart, Store,
  ClipboardList, Calendar, MessageSquare, BookOpen, Award, Tag, Globe,
  Inbox, CheckSquare,
} from 'lucide-react';
import { usePermissions } from '../../context/PermissionsContext';
import { useTranslation } from '../../i18n';

// ERPNext-style module groupings. Each group renders as a section.
const MODULES = [
  {
    section: 'Home',
    items: [
      { to: '/', label: 'Home', icon: LayoutDashboard, perm: null },
      { to: '/inbox', label: 'My Inbox', icon: Inbox, perm: null },
      { to: '/todos', label: 'My ToDos', icon: CheckSquare, perm: null },
      { to: '/erp-dashboards', label: 'Dashboards', icon: BarChart3, perm: null },
      { to: '/dashboard/executive', label: 'Executive Dashboard', icon: BarChart3, perm: 'reports_view' },
    ],
  },
  {
    section: 'Selling',
    items: [
      { to: '/sales', label: 'Sales', icon: ShoppingCart, perm: 'sales_view' },
      { to: '/pos', label: 'Point of Sale', icon: Store, perm: 'sales_view' },
      { to: '/customers', label: 'Customers', icon: Users, perm: 'sales_view' },
      { to: '/quotations', label: 'Quotations', icon: FileText, perm: 'sales_view' },
    ],
  },
  {
    section: 'Buying',
    items: [
      { to: '/purchase-orders', label: 'Purchase Orders', icon: ShoppingCart, perm: 'po_view' },
      { to: '/suppliers', label: 'Suppliers', icon: Building2, perm: 'suppliers_view' },
      { to: '/receiving', label: 'Receiving', icon: Truck, perm: 'po_view' },
    ],
  },
  {
    section: 'Stock',
    items: [
      { to: '/inventory', label: 'Inventory', icon: Package, perm: 'inventory_view' },
      { to: '/stock-transfers', label: 'Stock Transfers', icon: Truck, perm: 'transfer_view' },
      { to: '/recipes', label: 'Items & Recipes', icon: ChefHat, perm: 'recipes_view' },
      { to: '/production', label: 'Production', icon: Factory, perm: 'production_log' },
    ],
  },
  {
    section: 'Accounts',
    items: [
      { to: '/finance', label: 'Accounting', icon: DollarSign, perm: 'finance_view' },
      { to: '/finance/invoices', label: 'Invoices', icon: Receipt, perm: 'finance_view' },
      { to: '/finance/bills', label: 'Bills', icon: FileText, perm: 'finance_view' },
      { to: '/finance/banking', label: 'Banking', icon: CreditCard, perm: 'finance_view' },
      { to: '/finance/expenses', label: 'Expenses', icon: Receipt, perm: 'finance_view' },
      { to: '/finance/reports', label: 'Reports', icon: BarChart3, perm: 'reports_view' },
    ],
  },
  {
    section: 'Human Resources',
    items: [
      { to: '/hr', label: 'HR Home', icon: Briefcase, perm: 'hr_view' },
      { to: '/hr/employees', label: 'Employees', icon: Users, perm: 'hr_view' },
      { to: '/hr/leave', label: 'Leave', icon: Calendar, perm: 'hr_leave_manage' },
      { to: '/hr/attendance', label: 'Attendance', icon: ClipboardList, perm: 'hr_attendance_manage' },
      { to: '/hr/payroll', label: 'Payroll', icon: DollarSign, perm: 'hr_payroll_view' },
      { to: '/hr/performance', label: 'Performance', icon: Award, perm: 'hr_performance_manage' },
      { to: '/hr/recruitment', label: 'Recruitment', icon: UserCog, perm: 'hr_view' },
    ],
  },
  {
    section: 'CRM',
    items: [
      { to: '/crm', label: 'Customer Registry', icon: Heart, perm: 'sales_view' },
      { to: '/crm/communications', label: 'Communications', icon: MessageSquare, perm: 'sales_view' },
    ],
  },
  {
    section: 'System',
    items: [
      { to: '/branches', label: 'Branches', icon: Globe, perm: 'branches_view' },
      { to: '/master-data', label: 'Master Data', icon: Database, perm: 'module_master_data' },
      { to: '/doctype-builder', label: 'DocType Builder', icon: Database, perm: 'module_admin' },
      { to: '/report-builder', label: 'Report Builder', icon: BarChart3, perm: 'reports_create_custom' },
      { to: '/industry-templates', label: 'Industry Templates', icon: ChefHat, perm: 'module_admin' },
      { to: '/admin-console', label: 'Admin Console', icon: Shield, perm: 'module_admin' },
      { to: '/users', label: 'Users & Roles', icon: Shield, perm: 'users_view' },
      { to: '/notifications', label: 'Notifications', icon: Bell, perm: null },
      { to: '/settings', label: 'Settings', icon: Settings, perm: 'settings_view' },
    ],
  },
];

export default function ERPNextSidebar({ collapsed }) {
  const { can } = usePermissions();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const groups = useMemo(
    () => MODULES.map(g => ({
      ...g,
      items: g.items.filter(it => !it.perm || can(it.perm)),
    })).filter(g => g.items.length > 0),
    [can]
  );

  return (
    <aside className="erp-sidebar" aria-label="Primary navigation">
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--erp-border)' }}>
        <button
          onClick={() => navigate('/')}
          style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--erp-text)', fontWeight: 600, fontSize: 14 }}
          aria-label="Office Suite home"
        >
          <div style={{ width: 28, height: 28, background: 'var(--erp-primary)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700 }}>O</div>
          {!collapsed && <span>Office Suite</span>}
        </button>
      </div>
      {groups.map(group => (
        <div key={group.section}>
          <div className="erp-sidebar-section">{t(group.section)}</div>
          {group.items.map(item => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `erp-sidebar-item${isActive ? ' active' : ''}`}
                style={{ position: 'relative', textDecoration: 'none' }}
                end={item.to === '/'}
              >
                <Icon />
                <span className="erp-sidebar-label">{t(item.label)}</span>
              </NavLink>
            );
          })}
        </div>
      ))}
    </aside>
  );
}
